import React, { useState } from 'react';
import styled from 'styled-components';
import { FiUpload, FiCheck, FiAlertCircle, FiX, FiDownload } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--background-primary);
  z-index: 10;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const UploadArea = styled.div`
  border: 2px dashed var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-secondary);

  &:hover {
    border-color: var(--primary-color);
    background: rgba(var(--primary-color-rgb), 0.05);
  }

  &.dragging {
    border-color: var(--primary-color);
    background: rgba(var(--primary-color-rgb), 0.1);
  }

  input[type='file'] {
    display: none;
  }
`;

const UploadIcon = styled(FiUpload)`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const UploadText = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-weight: 500;
`;

const UploadSubText = styled.p`
  margin: 0.25rem 0 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const Summary = styled.div`
  background: var(--background-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const SummaryItem = styled.div`
  text-align: center;

  .label {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }

  .value {
    color: var(--text-primary);
    font-size: 1.5rem;
    font-weight: 700;
  }

  &.success .value {
    color: #4caf50;
  }

  &.error .value {
    color: #f44336;
  }

  &.warning .value {
    color: #ff9800;
  }
`;

const PreviewTable = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  thead {
    position: sticky;
    top: 0;
    background: var(--background-secondary);
  }

  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
  }

  th {
    font-weight: 600;
    color: var(--text-primary);
  }

  tbody tr {
    &:hover {
      background: var(--background-secondary);
    }

    &.invalid {
      background: rgba(244, 67, 54, 0.1);
    }

    &.update {
      background: rgba(255, 152, 0, 0.1);
    }
  }

  .valid {
    color: #4caf50;
  }

  .invalid {
    color: #f44336;
  }

  .update {
    color: #ff9800;
  }
`;

const ErrorList = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid #f44336;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;

  .error-title {
    color: #f44336;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .error-item {
    color: var(--text-primary);
    font-size: 0.9rem;
    margin: 0.25rem 0;
  }
`;

const DownloadLink = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    color: white;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  &.secondary {
    background: transparent;
    border: 1px solid var(--border-color);
    color: var(--text-primary);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CSVScoreUpload = ({
  itemId,
  itemType,
  courseId,
  itemTitle,
  onUploadComplete,
  onClose
}) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = async (selectedFile) => {
    setError(null);

    // Preview the file
    try {
      setUploading(true);
      const uploadMethod = itemType === 'assignment'
        ? apiService.uploadAssignmentScores
        : apiService.uploadQuizScores;

      const result = await uploadMethod.call(apiService, itemId, courseId, selectedFile);

      if (result.success === false && result.errors) {
        setError('Validation failed: ' + result.errors.map(e => e.msg).join(', '));
      } else {
        setPreview(result);
      }
    } catch (err) {
      logger.error('Error previewing CSV', err);
      setError(err.message || 'Failed to preview CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !preview.filePath) {
      setError('Invalid preview data');
      return;
    }

    try {
      setConfirming(true);
      const confirmMethod = itemType === 'assignment'
        ? apiService.confirmAssignmentScores
        : apiService.confirmQuizScores;

      const result = await confirmMethod.call(
        apiService,
        itemId,
        courseId,
        preview.filePath
      );

      if (result.success) {
        onUploadComplete?.(result);
        onClose?.();
      } else {
        setError(result.message || 'Failed to confirm scores');
      }
    } catch (err) {
      logger.error('Error confirming scores', err);
      setError(err.message || 'Failed to confirm and apply scores');
    } finally {
      setConfirming(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadScoresTemplate(itemType, itemId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${itemType}_${itemId}_scores_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading template', err);
      setError('Failed to download CSV template');
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>Upload Scores for {itemTitle}</h2>
          <CloseButton onClick={onClose} disabled={uploading || confirming}>
            <FiX size={24} />
          </CloseButton>
        </Header>

        <Content>
          {error && (
            <ErrorList>
              <div className="error-title">
                <FiAlertCircle style={{ display: 'inline', marginRight: '0.5rem' }} />
                Error
              </div>
              <div className="error-item">{error}</div>
            </ErrorList>
          )}

          {!preview ? (
            <Section>
              <SectionTitle>Step 1: Upload CSV File</SectionTitle>
              <UploadArea
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={dragActive ? 'dragging' : ''}
                onClick={() => document.getElementById('csv-input')?.click()}
              >
                <UploadIcon />
                <UploadText>
                  {uploading ? (
                    <>
                      <LoadingSpinner /> Validating CSV...
                    </>
                  ) : (
                    <>Drag and drop your CSV file here</>
                  )}
                </UploadText>
                <UploadSubText>
                  or click to select a file (CSV format required)
                </UploadSubText>
                <input
                  id="csv-input"
                  type="file"
                  accept=".csv"
                  onChange={handleInputChange}
                  disabled={uploading}
                />
              </UploadArea>

              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <DownloadLink onClick={handleDownloadTemplate} type="button">
                  <FiDownload size={16} />
                  Download CSV Template
                </DownloadLink>
              </div>
            </Section>
          ) : (
            <>
              <Section>
                <SectionTitle>Step 2: Review Preview</SectionTitle>

                {preview.summary && (
                  <Summary>
                    <SummaryItem>
                      <div className="label">Total Rows</div>
                      <div className="value">{preview.summary.totalRows}</div>
                    </SummaryItem>
                    <SummaryItem className="success">
                      <div className="label">Valid</div>
                      <div className="value">{preview.summary.validRows}</div>
                    </SummaryItem>
                    <SummaryItem className={preview.summary.invalidRows > 0 ? 'error' : ''}>
                      <div className="label">Invalid</div>
                      <div className="value">{preview.summary.invalidRows}</div>
                    </SummaryItem>
                    {preview.summary.toCreate > 0 && (
                      <SummaryItem>
                        <div className="label">To Create</div>
                        <div className="value">{preview.summary.toCreate}</div>
                      </SummaryItem>
                    )}
                    {preview.summary.toUpdate > 0 && (
                      <SummaryItem className="warning">
                        <div className="label">To Update</div>
                        <div className="value">{preview.summary.toUpdate}</div>
                      </SummaryItem>
                    )}
                  </Summary>
                )}

                {preview.summary?.errors && preview.summary.errors.length > 0 && (
                  <ErrorList>
                    <div className="error-title">
                      {preview.summary.errors.length} Validation Error{preview.summary.errors.length !== 1 ? 's' : ''}
                    </div>
                    {preview.summary.errors.slice(0, 10).map((err, idx) => (
                      <div key={idx} className="error-item">
                        {err}
                      </div>
                    ))}
                    {preview.summary.errors.length > 10 && (
                      <div className="error-item">
                        ... and {preview.summary.errors.length - 10} more errors
                      </div>
                    )}
                  </ErrorList>
                )}

                {preview.preview && preview.preview.length > 0 && (
                  <PreviewTable>
                    <Table>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Score</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.slice(0, 20).map((row, idx) => (
                          <tr
                            key={idx}
                            className={
                              row.isValid ? (row.willUpdate ? 'update' : '') : 'invalid'
                            }
                          >
                            <td>{row.email}</td>
                            <td>{row.score}</td>
                            <td className={row.isValid ? 'valid' : 'invalid'}>
                              {row.isValid ? (
                                row.willUpdate ? '📝 Update' : '✓ Create'
                              ) : (
                                <>
                                  <FiAlertCircle style={{ display: 'inline', marginRight: '0.25rem' }} />
                                  Invalid
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </PreviewTable>
                )}
              </Section>
            </>
          )}

          <ButtonGroup>
            <Button
              className="secondary"
              onClick={onClose}
              disabled={uploading || confirming}
            >
              Cancel
            </Button>
            {preview && (
              <Button
                className="secondary"
                onClick={() => {
                  setPreview(null);
                  setError(null);
                }}
                disabled={uploading || confirming}
              >
                Choose Different File
              </Button>
            )}
            {preview && !preview.summary?.hasErrors && (
              <Button
                className="primary"
                onClick={handleConfirm}
                disabled={confirming || uploading}
              >
                {confirming ? (
                  <>
                    <LoadingSpinner /> Uploading...
                  </>
                ) : (
                  <>
                    <FiCheck size={18} />
                    Confirm & Apply
                  </>
                )}
              </Button>
            )}
          </ButtonGroup>
        </Content>
      </Container>
    </Overlay>
  );
};

export default CSVScoreUpload;
